import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { TrendingUp, Users, Search, Star } from 'lucide-react';
import { PageHeader } from '@/components/common/PageHeader';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import api from '@/lib/axios';
import { API_ENDPOINTS } from '@/constants/api-endpoints';

interface DriverEarning {
  driver_profile_id: string;
  user_id: string;
  name: string | null;
  phone: string | null;
  wallet_balance: number;
  total_trips: number;
  average_rating: number;
  total_earnings: number;
  total_withdrawn: number;
  pending_withdrawals: number;
}
interface EarningsResp {
  summary: { total_driver_earnings: number; driver_count: number };
  drivers: DriverEarning[];
  total: number; page: number; limit: number; total_pages: number;
}

const inr = (n: number | string) => `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;

export function DriverEarningsPage() {
  const [search, setSearch] = useState('');
  const [applied, setApplied] = useState('');
  const [page, setPage] = useState(1);

  const { data, isLoading } = useQuery({
    queryKey: ['driver-earnings', applied, page],
    queryFn: async (): Promise<EarningsResp> => {
      const res = await api.get(API_ENDPOINTS.PAYMENTS.DRIVER_EARNINGS, {
        params: { search: applied || undefined, page, limit: 50 },
      });
      return (res.data as any)?.data ?? res.data;
    },
  });

  const s = data?.summary;

  return (
    <div className="space-y-6">
      <PageHeader title="Driver Earnings" description="Per-driver lifetime earnings, wallet balance, and withdrawals" />

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <Card><CardContent className="p-4 flex items-center gap-3">
          <TrendingUp className="h-5 w-5 text-emerald-600" />
          <div><div className="text-xs text-muted-foreground">Total Driver Earnings</div>
          <div className="text-lg font-bold">{inr(s?.total_driver_earnings ?? 0)}</div></div>
        </CardContent></Card>
        <Card><CardContent className="p-4 flex items-center gap-3">
          <Users className="h-5 w-5 text-blue-600" />
          <div><div className="text-xs text-muted-foreground">Drivers</div>
          <div className="text-lg font-bold">{s?.driver_count ?? 0}</div></div>
        </CardContent></Card>
      </div>

      <Card>
        <CardContent className="p-4">
          <div className="flex gap-2">
            <Input placeholder="Search driver by name or phone" value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { setPage(1); setApplied(search); } }} />
            <Button onClick={() => { setPage(1); setApplied(search); }}>
              <Search className="h-4 w-4 mr-2" /> Search
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex justify-center py-12"><Spinner /></div>
          ) : !data?.drivers?.length ? (
            <div className="text-center py-12 text-muted-foreground">No drivers found.</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Driver</TableHead>
                  <TableHead className="text-center">Trips</TableHead>
                  <TableHead className="text-center">Rating</TableHead>
                  <TableHead className="text-right">Total Earned</TableHead>
                  <TableHead className="text-right">Wallet</TableHead>
                  <TableHead className="text-right">Withdrawn</TableHead>
                  <TableHead className="text-right">Pending Payout</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.drivers.map((d) => (
                  <TableRow key={d.driver_profile_id}>
                    <TableCell>
                      <div className="font-medium">{d.name || 'Unknown'}</div>
                      <div className="text-xs text-muted-foreground">{d.phone || ''}</div>
                    </TableCell>
                    <TableCell className="text-center">{d.total_trips ?? 0}</TableCell>
                    <TableCell className="text-center">
                      <span className="inline-flex items-center gap-1">
                        <Star className="h-3.5 w-3.5 text-amber-500" />
                        {Number(d.average_rating || 0).toFixed(1)}
                      </span>
                    </TableCell>
                    <TableCell className="text-right font-semibold text-emerald-600">{inr(d.total_earnings)}</TableCell>
                    <TableCell className="text-right">
                      <span className={Number(d.wallet_balance) < 0 ? 'text-red-600 font-medium' : ''}>{inr(d.wallet_balance)}</span>
                    </TableCell>
                    <TableCell className="text-right">{inr(d.total_withdrawn)}</TableCell>
                    <TableCell className="text-right">{Number(d.pending_withdrawals) > 0 ? <span className="text-amber-600">{inr(d.pending_withdrawals)}</span> : '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {data && data.total_pages > 1 && (
        <div className="flex items-center justify-end gap-2">
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Prev</Button>
          <span className="text-sm text-muted-foreground">Page {page} of {data.total_pages}</span>
          <Button variant="outline" size="sm" disabled={page >= data.total_pages} onClick={() => setPage(p => p + 1)}>Next</Button>
        </div>
      )}
    </div>
  );
}
